import React, { useMemo } from 'react';

export default function EquityCurve({ trades, height = 220 }) {
  const data = useMemo(() => {
    if (!trades || trades.length === 0) return [];
    const sorted = [...trades].sort((a, b) => new Date(a.trade_date) - new Date(b.trade_date));
    let cumulative = 0;
    return sorted.map((t, i) => {
      cumulative += parseFloat(t.r_multiple) || 0;
      return { x: i, y: cumulative, date: t.trade_date, outcome: t.outcome };
    });
  }, [trades]);

  if (data.length < 2) {
    return (
      <div style={{textAlign:'center', padding:'40px 20px', color:'var(--text-4)'}}>
        <div style={{fontSize:32, marginBottom:8}}>📈</div>
        <p style={{fontSize:13}}>Log at least 2 trades to see your equity curve.</p>
      </div>
    );
  }

  const width = 800;
  const padding = { top: 20, right: 20, bottom: 30, left: 50 };
  const innerW = width - padding.left - padding.right;
  const innerH = height - padding.top - padding.bottom;

  const yMin = Math.min(0, ...data.map(d => d.y));
  const yMax = Math.max(0, ...data.map(d => d.y));
  const yRange = yMax - yMin || 1;

  const xScale = (i) => padding.left + (i / Math.max(data.length - 1, 1)) * innerW;
  const yScale = (v) => padding.top + innerH - ((v - yMin) / yRange) * innerH;

  const pathD = data.map((d, i) => `${i === 0 ? 'M' : 'L'} ${xScale(i)} ${yScale(d.y)}`).join(' ');
  const areaD = `${pathD} L ${xScale(data.length - 1)} ${yScale(0)} L ${xScale(0)} ${yScale(0)} Z`;

  const zeroY = yScale(0);
  const finalR = data[data.length - 1].y;
  const peak = Math.max(...data.map(d => d.y));
  let maxDD = 0;
  let runningPeak = data[0].y;
  data.forEach(d => {
    if (d.y > runningPeak) runningPeak = d.y;
    const dd = runningPeak - d.y;
    if (dd > maxDD) maxDD = dd;
  });

  // Y axis ticks
  const yTickStep = yRange / 4;
  const yTicks = [yMin, yMin + yTickStep, yMin + yTickStep*2, yMin + yTickStep*3, yMax];

  return (
    <div>
      <div style={{display:'flex',gap:24,marginBottom:12,flexWrap:'wrap'}}>
        <div>
          <div className="kpi-label">CUMULATIVE R</div>
          <div className="kpi-value" style={{color: finalR >= 0 ? 'var(--green)' : 'var(--red)'}}>
            {finalR > 0 ? '+' : ''}{finalR.toFixed(2)}R
          </div>
        </div>
        <div>
          <div className="kpi-label">PEAK</div>
          <div className="kpi-value">+{peak.toFixed(2)}R</div>
        </div>
        <div>
          <div className="kpi-label">MAX DRAWDOWN</div>
          <div className="kpi-value" style={{color:'var(--red)'}}>-{maxDD.toFixed(2)}R</div>
        </div>
      </div>
      <div style={{width:'100%', overflowX:'auto'}}>
        <svg viewBox={`0 0 ${width} ${height}`} style={{width:'100%', minWidth:'500px', display:'block'}}>
          <defs>
            <linearGradient id="eqGrad" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#C9973A" stopOpacity="0.4"/>
              <stop offset="100%" stopColor="#C9973A" stopOpacity="0"/>
            </linearGradient>
          </defs>

          {/* Y grid lines */}
          {yTicks.map((tick, i) => (
            <g key={i}>
              <line
                x1={padding.left}
                y1={yScale(tick)}
                x2={width - padding.right}
                y2={yScale(tick)}
                stroke="rgba(255,255,255,0.04)"
                strokeWidth="1"
              />
              <text
                x={padding.left - 8}
                y={yScale(tick) + 4}
                textAnchor="end"
                fontSize="10"
                fill="#5a5248"
                fontFamily="DM Mono, monospace"
              >
                {tick.toFixed(1)}R
              </text>
            </g>
          ))}

          {/* Zero line */}
          {yMin < 0 && (
            <line
              x1={padding.left}
              y1={zeroY}
              x2={width - padding.right}
              y2={zeroY}
              stroke="rgba(255,255,255,0.15)"
              strokeWidth="1"
              strokeDasharray="3,3"
            />
          )}

          {/* Area fill */}
          <path d={areaD} fill="url(#eqGrad)" />

          {/* Line */}
          <path d={pathD} fill="none" stroke="#C9973A" strokeWidth="2" />

          {/* Points */}
          {data.map((d, i) => (
            <circle
              key={i}
              cx={xScale(i)}
              cy={yScale(d.y)}
              r="3"
              fill={d.outcome === 'Win' ? '#3ecf8e' : d.outcome === 'Loss' ? '#e05252' : '#8a8070'}
            />
          ))}

          {/* X axis label */}
          <text x={padding.left} y={height - 8} fontSize="10" fill="#5a5248" fontFamily="DM Mono, monospace">
            Trade #1
          </text>
          <text x={width - padding.right} y={height - 8} fontSize="10" fill="#5a5248" textAnchor="end" fontFamily="DM Mono, monospace">
            Trade #{data.length}
          </text>
        </svg>
      </div>
    </div>
  );
}
